import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  NEWS_FILTER_PARAMS,
  ROUTES,
} from 'volto-ministerio-publico-federal/config/routes.constants';
import { decodeVocabularyToken } from 'volto-ministerio-publico-federal/utils/decodeVocabularyToken';

export interface NewsFilters {
  /** Temas (vocabulário `noticiatemas`, índice `tema`); na URL, `?tema=A&tema=B`. */
  subject: string[];
  unidade: string;
  searchable_text: string;
  /** Publicação (índice `effective`), em ISO — ver `utils/brDate`. */
  date_start: string;
  date_end: string;
  /** Saiu do formulário no redesenho, mas segue na URL por link antigo. */
  sort_order: string;
}

export const DEFAULT_NEWS_FILTERS: NewsFilters = {
  subject: [],
  unidade: '',
  searchable_text: '',
  date_start: '',
  date_end: '',
  sort_order: 'descending',
};

/** Teto do campo de busca; vale também para o termo que chega pela URL. */
export const SEARCHABLE_TEXT_MAX_LENGTH = 255;

export const parseNewsFilters = (search: string): NewsFilters => {
  const params = new URLSearchParams(search);

  return {
    // `getAll` cobre também o link de um tema só que `buildNewsFilterUrl` gera.
    subject: params.getAll(NEWS_FILTER_PARAMS.tema).filter(Boolean),
    unidade:
      params.get(NEWS_FILTER_PARAMS.unidade) || DEFAULT_NEWS_FILTERS.unidade,
    searchable_text: (
      params.get(NEWS_FILTER_PARAMS.searchableText) ||
      DEFAULT_NEWS_FILTERS.searchable_text
    ).slice(0, SEARCHABLE_TEXT_MAX_LENGTH),
    date_start:
      params.get(NEWS_FILTER_PARAMS.dataInicio) ||
      DEFAULT_NEWS_FILTERS.date_start,
    date_end:
      params.get(NEWS_FILTER_PARAMS.dataFim) || DEFAULT_NEWS_FILTERS.date_end,
    sort_order:
      params.get(NEWS_FILTER_PARAMS.sortOrder) ||
      DEFAULT_NEWS_FILTERS.sort_order,
  };
};

export const buildNewsFiltersSearch = (filters: NewsFilters): string => {
  const params = new URLSearchParams();

  filters.subject.filter(Boolean).forEach((tema) => {
    params.append(NEWS_FILTER_PARAMS.tema, tema);
  });
  if (filters.unidade) {
    params.set(NEWS_FILTER_PARAMS.unidade, filters.unidade);
  }
  if (filters.searchable_text) {
    params.set(NEWS_FILTER_PARAMS.searchableText, filters.searchable_text);
  }
  if (filters.date_start) {
    params.set(NEWS_FILTER_PARAMS.dataInicio, filters.date_start);
  }
  if (filters.date_end) {
    params.set(NEWS_FILTER_PARAMS.dataFim, filters.date_end);
  }
  // O padrão fica fora da URL para não poluir o link compartilhado.
  if (filters.sort_order !== DEFAULT_NEWS_FILTERS.sort_order) {
    params.set(NEWS_FILTER_PARAMS.sortOrder, filters.sort_order);
  }

  return params.toString();
};

/**
 * URL da listagem completa com o filtro já aplicado.
 *
 * Os valores são os mesmos tokens dos vocabulários usados na busca, então o
 * formulário de filtros reconhece o que vem pela URL sem conversão.
 */
export const buildNewsFilterUrl = ({
  tema,
  unidade,
}: {
  tema?: string;
  unidade?: string;
}): string => {
  const params = new URLSearchParams();

  if (tema) {
    // O bloco "Notícias por tema" guarda o token do vocabulário, que vem com
    // os acentos escapados (`Direitos do Cidad\xe3o`); o formulário da
    // listagem e os cards usam o rótulo. Normalizamos para o valor, senão o
    // mesmo filtro geraria dois links diferentes — e um deles ilegível.
    params.set(NEWS_FILTER_PARAMS.tema, decodeVocabularyToken(tema));
  }

  if (unidade) {
    params.set(NEWS_FILTER_PARAMS.unidade, unidade);
  }

  const search = params.toString();

  return search
    ? `${ROUTES.COMUNICACAO_NOTICIAS}?${search}`
    : ROUTES.COMUNICACAO_NOTICIAS;
};

/**
 * Monta o link de um tema preservando o recorte de unidade do contexto.
 *
 * Clicar no tema não deve jogar o leitor para fora de onde ele estava: se ele
 * navega dentro de uma unidade, o link mantém a unidade; numa listagem geral,
 * leva só o tema.
 *
 * `unidadePadrao` existe para o bloco "Notícias por tema", cujo contexto é o
 * que o editor configurou no bloco, não a querystring da página que o hospeda.
 */
export const useTemaHref = (unidadePadrao?: string) => {
  const location = useLocation();

  const unidade = useMemo(() => {
    if (unidadePadrao) {
      return unidadePadrao;
    }

    return parseNewsFilters(location.search).unidade;
  }, [unidadePadrao, location.search]);

  return useCallback(
    (tema: string) => buildNewsFilterUrl({ tema, unidade }),
    [unidade],
  );
};

/**
 * Filtros da listagem de notícias lidos e escritos na querystring.
 *
 * Antes eles viviam em `useState` dentro da view: o link da lista filtrada não
 * era compartilhável, o F5 devolvia a lista inteira e não havia como chegar
 * filtrado de fora — que é justamente o que o botão do bloco "Notícias por
 * tema" precisa fazer.
 *
 * `setFilters` não repassa `b_start`, então trocar de filtro sempre volta para
 * a primeira página (a paginação continua toda em `useNewsListing`).
 */
export const useNewsFilters = () => {
  const location = useLocation();
  const history = useHistory();

  const filters = useMemo(
    () => parseNewsFilters(location.search),
    [location.search],
  );

  const setFilters = useCallback(
    (next: NewsFilters) => {
      history.push({
        pathname: location.pathname,
        search: buildNewsFiltersSearch(next),
      });
    },
    [history, location.pathname],
  );

  return { filters, setFilters };
};

export default useNewsFilters;
